import { format, parseISO, isValid } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { Popover, Transition } from '@headlessui/react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Fragment } from 'react';
import "react-day-picker/dist/style.css?__remix_sideEffect__";

interface DatePickerProps {
  selected?: Date | string;
  onSelect: (date: Date | undefined) => void;
  minDate?: Date;
  placeholder?: string;
  className?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  align?: 'start' | 'end';
}

export function DatePicker({ selected, onSelect, minDate, placeholder = "Select date", className = "", isInvalid = false, errorMessage, align = 'start' }: DatePickerProps) {
  let selectedDate: Date | undefined = undefined;
  if (selected) {
    if (selected instanceof Date) {
      selectedDate = selected;
    } else {
      const parsed = parseISO(selected);
      if (isValid(parsed)) selectedDate = parsed;
    }
  }

  // Determine the month to show: selected date or current month or minDate
  const defaultMonth = selectedDate || new Date();

  return (
    <div className={className}>
            <Popover className="relative">
                {({ open }) =>
        <>
                        <Popover.Button className={`
                            w-full flex items-center justify-between bg-white border rounded-lg px-3 py-3 text-left shadow-sm transition-colors
                            ${isInvalid || errorMessage ? 'border-red-500 ring-1 ring-red-500' : open ? 'border-[#E80245] ring-1 ring-[#E80245]' : 'border-gray-200 hover:border-gray-300'}
                        `}>
                            <span className={`block text-base ${selectedDate ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                                {selectedDate ? format(selectedDate, 'dd/MM/yyyy') : placeholder}
                            </span>
                            <CalendarIcon className="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" />
                        </Popover.Button>

                        <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1">
            
                            <Popover.Panel className={`absolute z-50 mt-2 p-4 bg-white rounded-xl shadow-xl border border-gray-100 w-[340px] max-w-[90vw] sm:max-w-md ${align === 'end' ? 'right-0' : 'left-0'}`}>
                                <style>{`
                                    .rdp { --rdp-cell-size: 45px; --rdp-accent-color: #E80245; --rdp-background-color: #FFF0F3; margin: 0; }
                                    .rdp-day_selected:not([disabled]) { background-color: #E80245; color: white; font-weight: bold; }
                                    .rdp-day_selected:hover:not([disabled]) { background-color: #D0023D; }
                                    .rdp-button:hover:not([disabled]):not(.rdp-day_selected) { background-color: #FFF0F3; color: #E80245; }
                                    .rdp-day { font-size: 1rem; }
                                    .rdp-caption_label { font-size: 1.1rem; font-weight: 600; color: #111827; }
                                    .rdp-nav_button { width: 32px; height: 32px; }
                                    .rdp-chevron { fill: #E80245; }
                                    .rdp-root { --rdp-today-color: #E80245; --rdp-selected-border: 2px solid #E80245; }
                                `}</style>
                                <DayPicker
                mode="single"
                selected={selectedDate}
                defaultMonth={defaultMonth}
                onSelect={(d: Date | undefined) => {
                  onSelect(d);
                }}
                disabled={minDate ? { before: minDate } : undefined}
                showOutsideDays />
              
                            </Popover.Panel>
                        </Transition>
                    </>}
        
            </Popover>
            {errorMessage &&
      <p className="mt-1 text-xs text-red-500 font-medium">
                    {errorMessage}
                </p>}
      
        </div>);

}